import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { mockData } from '../data/mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { CheckCircle, XCircle, ArrowRight, ArrowLeft, Phone, User, Award, TrendingUp, Loader2, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORT_WHATSAPP = '16478745620';

const QuizModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', phone: '' });
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questions = mockData.quizQuestions;
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const content = {
    en: {
      title: "Free English Level Assessment",
      subtitle: "Answer 8 quick questions to discover your English level",
      question: "Question",
      of: "of",
      next: "Next Question",
      previous: "Previous",
      finish: "Finish Quiz",
      selectOption: "Please select an option",
      almostThere: "Almost There!",
      getResults: "Get Your Results",
      enterDetails: "Enter your details and our team will contact you on WhatsApp with your results and course recommendations.",
      name: "Full Name",
      phone: "WhatsApp Number",
      phonePlaceholder: "+966 5XX XXX XXXX",
      viewResults: "View My Results",
      yourLevel: "Your English Level",
      score: "Your Score",
      recommendations: "Recommended Courses",
      startLearning: "Browse Our Courses",
      retake: "Retake Quiz",
      fillFields: "Please fill in all fields",
      contactSoon: "Our team will contact you on WhatsApp soon!"
    },
    ar: {
      title: "تقييم مستوى اللغة الإنجليزية المجاني",
      subtitle: "أجب على 8 أسئلة سريعة لاكتشاف مستواك في اللغة الإنجليزية",
      question: "سؤال",
      of: "من",
      next: "السؤال التالي",
      previous: "السابق",
      finish: "إنهاء الاختبار",
      selectOption: "يرجى اختيار إجابة",
      almostThere: "أوشكت على الانتهاء!",
      getResults: "احصل على نتائجك",
      enterDetails: "أدخل بياناتك وسيتواصل معك فريقنا على واتساب بنتائجك والدورات الموصى بها.",
      name: "الاسم الكامل",
      phone: "رقم الواتساب",
      phonePlaceholder: "+966 5XX XXX XXXX",
      viewResults: "عرض نتائجي",
      yourLevel: "مستواك في اللغة الإنجليزية",
      score: "درجتك",
      recommendations: "الدورات الموصى بها",
      startLearning: "تصفح دوراتنا",
      retake: "إعادة الاختبار",
      fillFields: "يرجى ملء جميع الحقول",
      contactSoon: "سيتواصل معك فريقنا على واتساب قريباً!"
    }
  };

  const t = content[language];

  const handleOptionSelect = (optionIndex) => {
    setSelectedOption(optionIndex);
  };

  const handleNext = () => {
    if (selectedOption === null) {
      toast({
        title: t.selectOption,
        variant: "destructive"
      });
      return;
    }

    const newAnswers = [...answers];
    newAnswers[currentQuestion] = {
      questionId: questions[currentQuestion].id,
      points: questions[currentQuestion].options[selectedOption].points
    };
    setAnswers(newAnswers);
    setSelectedOption(null);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowLeadForm(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(null);
    }
  };

  const calculateResults = () => {
    const totalScore = answers.reduce((sum, answer) => sum + answer.points, 0);
    const level = mockData.quizLevels.find(
      l => totalScore >= l.minScore && totalScore <= l.maxScore
    );
    return { totalScore, level };
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    
    if (!leadData.name || !leadData.phone) {
      toast({
        title: t.fillFields,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Calculate results
      const { totalScore, level } = calculateResults();
      const levelName = level?.name?.[language] || level?.name?.en || 'Beginner';
      
      // Save to backend
      await axios.post(`${BACKEND_URL}/api/quiz/results`, {
        name: leadData.name,
        email: `${leadData.phone}@whatsapp.user`,
        phone: leadData.phone,
        answers: answers,
        total_score: totalScore,
        level: levelName,
        language: language
      });
      
      // Create WhatsApp message to send to customer support
      const whatsappMessage = language === 'ar' 
        ? `🎓 نتيجة اختبار جديدة!\n\n👤 الاسم: ${leadData.name}\n📱 الهاتف: ${leadData.phone}\n📊 الدرجة: ${totalScore}/24\n🏆 المستوى: ${levelName}\n\nيرجى التواصل مع هذا الطالب المحتمل.`
        : `🎓 New Quiz Result!\n\n👤 Name: ${leadData.name}\n📱 Phone: ${leadData.phone}\n📊 Score: ${totalScore}/24\n🏆 Level: ${levelName}\n\nPlease follow up with this potential student.`;
      
      // Open WhatsApp to send message to support
      const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(whatsappMessage)}`;
      window.open(whatsappUrl, '_blank');
      
      toast({
        title: t.contactSoon,
        className: "bg-green-600 text-white"
      });
      
    } catch (error) {
      console.error('Error submitting quiz:', error);
      // Still show results even if API fails
    }
    
    setIsSubmitting(false);
    setShowResults(true);
  };

  const handleRetake = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowLeadForm(false);
    setShowResults(false);
    setSelectedOption(null);
    setLeadData({ name: '', phone: '' });
  };

  const handleClose = () => {
    handleRetake();
    onClose();
  };

  const scrollToCourses = () => {
    handleClose();
    setTimeout(() => {
      const coursesSection = document.getElementById('courses');
      if (coursesSection) {
        coursesSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Lead Form View - Now just shows WhatsApp button to get results
  if (showLeadForm && !showResults) {
    const { totalScore, level } = calculateResults();
    const levelName = level?.name?.[language] || level?.name?.en || 'Beginner';
    
    const handleGetResults = () => {
      const message = language === 'ar'
        ? `🎓 مرحباً! لقد أكملت اختبار تحديد المستوى.\n📊 درجتي: ${totalScore}/24\n🏆 مستواي: ${levelName}\n\nأريد معرفة المزيد عن نتائجي والدورات الموصى بها.`
        : `🎓 Hello! I completed the level assessment.\n📊 My Score: ${totalScore}/24\n🏆 My Level: ${levelName}\n\nI want to know more about my results and recommended courses.`;
      
      const whatsappUrl = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      setShowResults(true);
    };
    
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              {t.almostThere}
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Award className="w-10 h-10 text-teal-600" />
            </div>
            
            {/* Score Preview */}
            <div className={`bg-gradient-to-r ${level.color} rounded-xl p-4 text-white mb-6`}>
              <p className="text-lg font-semibold">{t.score}: {totalScore} / 24</p>
            </div>
            
            <p className="text-gray-600 mb-6">
              {language === 'ar' 
                ? 'انقر على الزر أدناه للحصول على نتائجك الكاملة وتوصيات الدورات على واتساب'
                : 'Click the button below to get your full results and course recommendations on WhatsApp'}
            </p>

            <Button
              onClick={handleGetResults}
              className="w-full bg-[#25D366] hover:bg-[#20BA5A] text-white py-6 text-lg"
            >
              <MessageCircle className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
              {language === 'ar' ? 'انقر لمعرفة نتائجك' : 'Click to Know Your Results'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Results View
  if (showResults) {
    const { totalScore, level } = calculateResults();
    
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-3xl font-bold text-center">
              {t.yourLevel}
            </DialogTitle>
          </DialogHeader>

          <div className="py-6 space-y-6">
            {/* Level Badge */}
            <div className={`bg-gradient-to-r ${level.color} rounded-2xl p-8 text-white text-center`}>
              <div className="text-6xl mb-4">{level.badge}</div>
              <h3 className="text-4xl font-bold mb-2">
                {language === 'en' ? level.level : level.levelAr}
              </h3>
              <p className="text-xl opacity-90">
                {t.score}: {totalScore} / 24
              </p>
            </div>

            {/* WhatsApp Notice */}
            <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-[#25D366] flex-shrink-0" />
              <p className="text-green-800 font-medium">
                {t.contactSoon}
              </p>
            </div>

            {/* Description */}
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed">
                {level.description[language]}
              </p>
            </div>

            {/* Recommended Courses */}
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">
                {t.recommendations}
              </h4>
              <div className="space-y-3">
                {level.recommendedCourses.map((course, index) => {
                  const fullCourse = mockData.courses.find(c => c.id === course.id);
                  if (!fullCourse) return null;
                  
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 bg-white rounded-xl border-2 border-teal-100 hover:border-teal-300 transition-colors"
                    >
                      <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                        <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0" />
                        <div>
                          <p className="font-semibold text-gray-900">
                            {fullCourse.title[language]}
                          </p>
                          <p className="text-sm text-teal-600 font-bold">
                            {fullCourse.price.current} {fullCourse.price.currency[language]}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={scrollToCourses}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white py-6 text-lg"
              >
                {t.startLearning}
              </Button>
              <Button
                onClick={handleRetake}
                variant="outline"
                className="flex-1 py-6 text-lg border-2"
              >
                {t.retake}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Questions View
  const currentQ = questions[currentQuestion];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center">
            {t.title}
          </DialogTitle>
          <p className="text-center text-gray-500 mt-2">{t.subtitle}</p>
        </DialogHeader>

        <div className="py-6">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>{t.question} {currentQuestion + 1} {t.of} {questions.length}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-3" />
          </div>

          {/* Question */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              {currentQ.question[language]}
            </h3>

            {/* Options */}
            <div className="space-y-3">
              {currentQ.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleOptionSelect(index)}
                  className={`w-full p-4 text-${language === 'ar' ? 'right' : 'left'} rounded-xl border-2 transition-all duration-200 ${
                    selectedOption === index
                      ? 'border-teal-500 bg-teal-50 text-teal-700'
                      : 'border-gray-200 hover:border-teal-300 hover:bg-gray-50'
                  }`}
                >
                  <div className={`flex items-center ${language === 'ar' ? 'space-x-reverse' : ''} space-x-3`}>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedOption === index
                        ? 'border-teal-500 bg-teal-500'
                        : 'border-gray-300'
                    }`}>
                      {selectedOption === index && (
                        <CheckCircle className="w-4 h-4 text-white" />
                      )}
                    </div>
                    <span className="text-lg">{option.text[language]}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              onClick={handlePrevious}
              variant="outline"
              disabled={currentQuestion === 0}
              className="flex items-center"
            >
              {language === 'ar' ? (
                <>
                  {t.previous}
                  <ArrowRight className="w-4 h-4 mr-2" />
                </>
              ) : (
                <>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t.previous}
                </>
              )}
            </Button>
            <Button
              onClick={handleNext}
              className="bg-teal-600 hover:bg-teal-700 text-white flex items-center"
            >
              {currentQuestion === questions.length - 1 ? t.finish : t.next}
              {language === 'ar' ? (
                <ArrowLeft className="w-4 h-4 mr-2" />
              ) : (
                <ArrowRight className="w-4 h-4 ml-2" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuizModal;
